// =========================================================================
// End Overlay (勝敗時のフルスクリーン overlay)
// =========================================================================
// spec/closing_river_web_mvp_v2.md 「End Overlay」「アニメーション」
//   「線質差カタログ」 に従う:
//
//   勝利:
//     - "Silent Clean Clear" / "Clean Clear" / "Silent Clear" / "Clear" の
//       いずれかを Badge から決定して表示
//     - 600ms フェードイン + ティールグロー
//     - Clean shimmer は勝利時のみ強調 (badge.clean が真の時)
//
//   敗北:
//     - 理由表示: 「手数超過」 / 「合成不能」 / 「非連結」
//     - 400ms フェードイン + --warn-soft 色
//
//   ボタン:
//     - 「もう一度 (同 board)」 → onRetry
//     - 「別の board」 → onPickAnother   (敗北時も表示するか spec は明言なし。
//        Phase 1 では勝利時のみ、敗北時は retry/home の 2 つに留める)
//     - 「タイトルへ」 → onHome
//
// 担当: W5
// =========================================================================

import type { Badge } from '../catalog/manifest.js';

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

export type EndOverlayResult =
  | {
      readonly kind: 'win';
      readonly badge: Badge;
      readonly movesUsed: number;
      readonly shortest: number;
      readonly N: number;
    }
  | {
      readonly kind: 'lose';
      readonly reason: 'turnLimit' | 'noEdges' | 'disconnected';
      readonly movesUsed: number;
      readonly N: number;
    };

export interface EndOverlayCallbacks {
  readonly onRetry: () => void;
  readonly onPickAnother: () => void;
  readonly onHome: () => void;
}

export interface EndOverlayHandle {
  show(result: EndOverlayResult, callbacks: EndOverlayCallbacks): void;
  hide(): void;
  destroy(): void;
}

// -------------------------------------------------------------------------
// 実装
// -------------------------------------------------------------------------

const OVERLAY_CLASS = 'cr-end-overlay';

/**
 * Badge から表示用タイトルを組み立てる。
 *
 *   silent && clean    → "Silent Clean Clear"
 *   silent && !clean   → "Silent Clear"
 *   !silent && clean   → "Clean Clear"
 *   !silent && !clean  → "Clear"
 *
 * Phase 2 で日本語化検討 (現状英語表記、numbers99 系統と揃え)。
 */
function winTitle(badge: Badge): string {
  if (badge.silent && badge.clean) return 'Silent Clean Clear';
  if (badge.silent) return 'Silent Clear';
  if (badge.clean) return 'Clean Clear';
  return 'Clear';
}

function loseTitle(reason: 'turnLimit' | 'noEdges' | 'disconnected'): string {
  switch (reason) {
    case 'turnLimit':
      return '手数超過';
    case 'noEdges':
      return '合成不能';
    case 'disconnected':
      return '非連結';
  }
}

export function createEndOverlay(container: HTMLElement): EndOverlayHandle {
  // overlay 要素は show 時に都度組み立てて remove 時に破棄する。
  // (state がほぼ無いので class を持たず関数 closure で保持)
  let current: HTMLElement | null = null;

  function buildOverlay(
    result: EndOverlayResult,
    callbacks: EndOverlayCallbacks,
  ): HTMLElement {
    const root = document.createElement('div');
    root.className = OVERLAY_CLASS;
    root.dataset.kind = result.kind;
    // overlay の基本スタイル。styles.css 側で cr-end-overlay を整える想定だが、
    // W1 確定の styles.css を触らない方針なので最低限を inline で持つ。
    // (本実装は W5 範囲。CSS は将来 W1 / styles.css に統合)
    root.style.cssText = [
      'position: fixed',
      'inset: 0',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: 24px',
      'padding: 32px',
      'background: rgba(10, 14, 20, 0.78)',
      'backdrop-filter: blur(6px)',
      '-webkit-backdrop-filter: blur(6px)',
      'z-index: 100',
      'opacity: 0',
      'transition: opacity ' +
        (result.kind === 'win' ? 'var(--t-slow)' : '400ms') +
        ' ease',
    ].join(';');

    // タイトル ----------------------------------------------------------
    const title = document.createElement('div');
    title.className = `${OVERLAY_CLASS}__title`;
    title.style.cssText = [
      'font-size: 32px',
      'font-weight: 600',
      'letter-spacing: 0.04em',
      'text-align: center',
      result.kind === 'win'
        ? 'color: var(--accent-strong); text-shadow: 0 0 24px var(--accent-dim), 0 0 8px var(--accent)'
        : 'color: var(--warn-soft)',
    ].join(';');
    title.textContent =
      result.kind === 'win' ? winTitle(result.badge) : loseTitle(result.reason);
    root.appendChild(title);

    // Clean shimmer (勝利 + clean badge のみ)
    if (result.kind === 'win' && result.badge.clean) {
      const shimmer = document.createElement('div');
      shimmer.className = `${OVERLAY_CLASS}__shimmer`;
      shimmer.style.cssText = [
        'position: absolute',
        'inset: 0',
        'pointer-events: none',
        'background: radial-gradient(circle at 50% 38%, rgba(127, 230, 223, 0.18) 0%, transparent 60%)',
        'opacity: 0',
        'animation: cr-clean-shimmer 1.4s ease-out 200ms forwards',
      ].join(';');
      root.appendChild(shimmer);
    }

    // 詳細 (moves / N) --------------------------------------------------
    const detail = document.createElement('div');
    detail.className = `${OVERLAY_CLASS}__detail`;
    detail.style.cssText = [
      'font-size: var(--font-base)',
      'color: var(--text-dim)',
      'text-align: center',
    ].join(';');
    if (result.kind === 'win') {
      const lines = [`${result.movesUsed} / ${result.N} 手`];
      if (result.badge.silent) {
        lines.push(`最短 ${result.shortest} 手達成`);
      } else {
        lines.push(`最短 ${result.shortest} 手`);
      }
      detail.innerHTML = lines.map((l) => `<div>${escapeText(l)}</div>`).join('');
    } else {
      detail.textContent = `${result.movesUsed} / ${result.N} 手`;
    }
    root.appendChild(detail);

    // ボタン群 ----------------------------------------------------------
    const buttons = document.createElement('div');
    buttons.className = `${OVERLAY_CLASS}__buttons`;
    buttons.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'gap: 12px',
      'width: 100%',
      'max-width: 280px',
      'margin-top: 12px',
    ].join(';');

    const retryBtn = makeButton('もう一度', () => {
      callbacks.onRetry();
    });
    buttons.appendChild(retryBtn);

    if (result.kind === 'win') {
      const pickBtn = makeButton('別の board', () => {
        callbacks.onPickAnother();
      });
      buttons.appendChild(pickBtn);
    }

    const homeBtn = makeButton('タイトルへ', () => {
      callbacks.onHome();
    });
    buttons.appendChild(homeBtn);

    root.appendChild(buttons);
    return root;
  }

  function show(result: EndOverlayResult, callbacks: EndOverlayCallbacks): void {
    // 既存 overlay があれば破棄してから再表示
    if (current) {
      current.remove();
      current = null;
    }
    ensureKeyframes();
    const el = buildOverlay(result, callbacks);
    container.appendChild(el);
    // 次フレームで opacity を 1 にしてフェードイン
    requestAnimationFrame(() => {
      el.style.opacity = '1';
    });
    current = el;

    // 勝利時のハプティック (spec 「ハプティック」 セクション)
    if (result.kind === 'win' && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate([30, 50, 30]);
      } catch {
        // iOS Safari など vibrate 実装無し → silent ignore
      }
    }
  }

  function hide(): void {
    if (!current) return;
    const el = current;
    current = null;
    el.style.opacity = '0';
    // フェードアウト後に remove
    window.setTimeout(() => {
      el.remove();
    }, 400);
  }

  function destroy(): void {
    if (current) {
      current.remove();
      current = null;
    }
  }

  return { show, hide, destroy };
}

// -------------------------------------------------------------------------
// helpers
// -------------------------------------------------------------------------

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.style.cssText = [
    'width: 100%',
    'padding: 14px 16px',
    'font-size: var(--font-base)',
    'border-radius: var(--radius-md)',
    'background: var(--bg-elev)',
    'border: 1px solid var(--border)',
    'color: var(--text)',
  ].join(';');
  btn.addEventListener('click', () => {
    onClick();
  });
  return btn;
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Clean shimmer 用 keyframes を <head> に一度だけ注入する。
 * styles.css を触らない方針なので、最低限の keyframes はこちらで持つ。
 * (W1 styles.css 統合は Phase 2 / W1 owner のリファクタ時に行う)
 */
let keyframesInjected = false;
function ensureKeyframes(): void {
  if (keyframesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-cr-end-overlay', 'keyframes');
  style.textContent = `
@keyframes cr-clean-shimmer {
  0%   { opacity: 0; transform: scale(0.92); }
  40%  { opacity: 1; }
  100% { opacity: 0; transform: scale(1.08); }
}
`;
  document.head.appendChild(style);
  keyframesInjected = true;
}
